import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';
import { Header } from '@/components/site/Header';
import { Footer } from '@/components/site/Footer';
import { CookieNotice } from '@/components/site/CookieNotice';
import HomePage from '@/pages/HomePage';
import AboutPage from '@/pages/AboutPage';
import CaseStudiesPage from '@/pages/CaseStudiesPage';
import CaseStudyDetailPage from '@/pages/CaseStudyDetailPage';
import OperationsPage from '@/pages/OperationsPage';
import PeoplePage from '@/pages/PeoplePage';
import GrowthPage from '@/pages/GrowthPage';
import LaunchPage from '@/pages/LaunchPage';
import EcosystemPage from '@/pages/EcosystemPage';
import EcosystemServicePage from '@/pages/EcosystemServicePage';
import InsightsPage from '@/pages/InsightsPage';
import InsightArticlePage from '@/pages/InsightArticlePage';
import ContactPage from '@/pages/ContactPage';
import BookingManagePage from '@/pages/BookingManagePage';
import ServicesPage from '@/pages/ServicesPage';
import PlaceholderPage from '@/pages/PlaceholderPage';
import ServiceDetailPage from '@/pages/ServiceDetailPage';
import LegalPage from '@/pages/LegalPage';
import CompassPage from '@/pages/CompassPage';
import PulsePage from '@/pages/PulsePage';
import ForeseePage from '@/pages/ForeseePage';
import ReachPage from '@/pages/ReachPage';
import { initSmoothScroll, scrollToTop } from '@/lib/scroll';
import { initLiveContent } from '@/lib/liveContent';
import AdminApp from '@/admin/AdminApp';
import PreviewPage from '@/pages/PreviewPage';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

// CMS-managed legal pages (content lives in cms_pages — Admin → Pages).
const LEGAL_ROUTES = [
  '/privacy-policy',
  '/cookie-policy',
  '/terms-of-service',
  '/data-protection',
  '/accessibility',
];

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Native scrollIntoView is the reliable path here: Lenis scrollTo can
      // silently no-op when a <Navigate> redirect swaps the location before
      // Lenis has finished its first frame. scroll-mt-* classes on sections
      // provide the sticky-header offset.
      let attempts = 0;
      const tryScroll = () => {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ block: 'start', behavior: 'auto' });
          return;
        }
        if (attempts++ < 60) requestAnimationFrame(tryScroll);
      };
      const t = setTimeout(() => requestAnimationFrame(tryScroll), 50);
      return () => clearTimeout(t);
    }
    scrollToTop();
  }, [pathname, hash]);
  return null;
};

// Permanent redirect: every old /blog/:slug article URL maps to its exact
// new /insights/:slug article — never just the Insights homepage.
const BlogArticleRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/insights/${slug}`} replace />;
};

function App() {
  useEffect(() => {
    initSmoothScroll();
    initLiveContent();
  }, []);

  return (
    <BrowserRouter>
      <ScrollManager />
      <SiteLayout />
    </BrowserRouter>
  );
}

// The admin area and draft previews render chromeless (no public
// Header/Footer); everything else gets the approved global chrome.
const SiteLayout = () => {
  const { pathname } = useLocation();
  const chromeless = pathname.startsWith('/admin') || pathname.startsWith('/preview');

  // Anonymous page-view counting on public routes only.
  useEffect(() => {
    if (!chromeless) trackEvent('page_view');
  }, [pathname, chromeless]);

  if (chromeless) {
    return (
      <div className="min-h-screen bg-slate-100 font-sans text-navy-900 antialiased">
        <Routes>
          <Route path="/admin/*" element={<AdminApp />} />
          <Route path="/preview/:resource/:slug" element={<PreviewPage />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans text-navy-900 antialiased">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[70] focus:rounded-md focus:bg-navy-900 focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-white"
      >
        Skip to main content
      </a>
      <Header />
      <main id="main-content" className="pt-20">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/services/operations" element={<OperationsPage />} />
            <Route path="/services/people" element={<PeoplePage />} />
            <Route path="/services/growth" element={<GrowthPage />} />
            <Route path="/services/launch" element={<LaunchPage />} />
            <Route path="/launch/:slug" element={<ServiceDetailPage key="launch" division="launch" />} />
            <Route path="/growth/:slug" element={<ServiceDetailPage key="growth" division="growth" />} />
            <Route path="/people/:slug" element={<ServiceDetailPage key="people" division="people" />} />
            <Route path="/ecosystem" element={<EcosystemPage />} />
            {/* Amden Compass has one canonical marketing page; the old
                ecosystem detail route and alternative URLs redirect to it. */}
            <Route path="/ecosystem/services/amden-compass" element={<Navigate to="/systems/compass" replace />} />
            <Route path="/amden-compass" element={<Navigate to="/systems/compass" replace />} />
            <Route path="/compass" element={<Navigate to="/systems/compass" replace />} />
            <Route path="/ecosystem/compass" element={<Navigate to="/systems/compass" replace />} />
            <Route path="/ecosystem/amden-compass" element={<Navigate to="/systems/compass" replace />} />
            <Route path="/services/compass" element={<Navigate to="/systems/compass" replace />} />
            <Route path="/systems/compass" element={<CompassPage />} />
            {/* Amden Pulse — canonical marketing page + legacy URL redirects */}
            <Route path="/ecosystem/services/amden-pulse" element={<Navigate to="/systems/pulse" replace />} />
            <Route path="/amden-pulse" element={<Navigate to="/systems/pulse" replace />} />
            <Route path="/pulse" element={<Navigate to="/systems/pulse" replace />} />
            <Route path="/ecosystem/pulse" element={<Navigate to="/systems/pulse" replace />} />
            <Route path="/ecosystem/amden-pulse" element={<Navigate to="/systems/pulse" replace />} />
            <Route path="/services/pulse" element={<Navigate to="/systems/pulse" replace />} />
            <Route path="/systems/pulse" element={<PulsePage />} />
            {/* Foresee — canonical marketing page + legacy URL redirects */}
            <Route path="/ecosystem/services/amden-foresee" element={<Navigate to="/systems/foresee" replace />} />
            <Route path="/amden-foresee" element={<Navigate to="/systems/foresee" replace />} />
            <Route path="/foresee" element={<Navigate to="/systems/foresee" replace />} />
            <Route path="/ecosystem/foresee" element={<Navigate to="/systems/foresee" replace />} />
            <Route path="/ecosystem/amden-foresee" element={<Navigate to="/systems/foresee" replace />} />
            <Route path="/services/foresee" element={<Navigate to="/systems/foresee" replace />} />
            <Route path="/beyond-compliance" element={<Navigate to="/systems/foresee" replace />} />
            <Route path="/systems/foresee" element={<ForeseePage />} />
            {/* Amden Reach — canonical page is /amden-reach (user-approved);
                the /systems and other legacy URLs redirect to it. */}
            <Route path="/ecosystem/services/amden-reach" element={<Navigate to="/amden-reach" replace />} />
            <Route path="/reach" element={<Navigate to="/amden-reach" replace />} />
            <Route path="/systems/reach" element={<Navigate to="/amden-reach" replace />} />
            <Route path="/ecosystem/reach" element={<Navigate to="/amden-reach" replace />} />
            <Route path="/ecosystem/amden-reach" element={<Navigate to="/amden-reach" replace />} />
            <Route path="/services/reach" element={<Navigate to="/amden-reach" replace />} />
            <Route path="/amden-reach" element={<ReachPage />} />
            <Route path="/ecosystem/services/:slug" element={<EcosystemServicePage />} />
            <Route path="/recruitment" element={<Navigate to="/ecosystem" replace />} />
            <Route path="/systems" element={<Navigate to="/ecosystem" replace />} />
            <Route path="/blog" element={<Navigate to="/insights" replace />} />
            <Route path="/blog/:slug" element={<BlogArticleRedirect />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/:slug" element={<InsightArticlePage />} />
            <Route path="/contact" element={<ContactPage />} />
            {/* /book-a-meeting is printed on QR codes and marketing materials — keep it working permanently */}
            <Route path="/book-a-meeting" element={<Navigate to="/contact" replace />} />
            <Route path="/book" element={<Navigate to="/contact#book-appointment" replace />} />
            <Route path="/book-consultation" element={<Navigate to="/contact#book-appointment" replace />} />
            <Route path="/contact/book" element={<Navigate to="/contact#book-appointment" replace />} />
            <Route path="/booking/manage/:reference" element={<BookingManagePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/systems/:slug" element={<PlaceholderPage title="System overview" notFound />} />
            {LEGAL_ROUTES.map((path) => (
              <Route key={path} path={path} element={<LegalPage slug={path.slice(1)} />} />
            ))}
            <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />
            <Route path="*" element={<PlaceholderPage title="Page not found" notFound />} />
          </Routes>
        </main>
        <Footer />
        <CookieNotice />
        <Toaster position="top-center" richColors />
      </div>
  );
}

export default App;
